import type { Dot } from '@hillchart/shared';

/**
 * Self-contained viewBox for the card thumbnail. We don't reuse the main
 * chart's 1000×400 viewBox because at card size (~120×48 displayed) a
 * stroke width of 2 logical units would render as ~0.25 CSS pixels — too
 * faint to read. Keeping a dedicated small viewBox lets us tune stroke
 * widths and dot radii in units that make sense at the preview's
 * rendered size.
 */
const PREVIEW = {
  width: 240,
  height: 72,
  paddingX: 10,
  paddingY: 8,
} as const;

const USABLE_W = PREVIEW.width - 2 * PREVIEW.paddingX;
const USABLE_H = PREVIEW.height - 2 * PREVIEW.paddingY;

function previewX(x: number): number {
  return PREVIEW.paddingX + (x / 100) * USABLE_W;
}

/**
 * Same sinusoid as the main chart (`y = sin(πx/100)`), scaled to the
 * preview viewBox. Duplicating a single `Math.sin` call rather than
 * parameterising `hill-math.ts` keeps both modules honest: the main
 * `<Hill />` never needs to render at preview dimensions, and the
 * preview never needs dragging / labels / CTM math.
 */
function previewY(x: number): number {
  return PREVIEW.height - PREVIEW.paddingY - USABLE_H * Math.sin((Math.PI * x) / 100);
}

/**
 * Pre-computed at module load — the curve never changes, so recomputing it
 * on every card render would just burn CPU on a long list of hills. A
 * polyline sampled every 4 units (26 points, 25 segments) is smoother than
 * the eye can resolve at this size.
 */
const PREVIEW_PATH = (() => {
  const segments: string[] = [];
  for (let x = 0; x <= 100; x += 4) {
    segments.push(`${x === 0 ? 'M' : 'L'} ${previewX(x).toFixed(2)} ${previewY(x).toFixed(2)}`);
  }
  return segments.join(' ');
})();

interface HillPreviewProps {
  dots: Dot[];
}

/**
 * Thumbnail of a hill chart — curve plus every dot — used inside
 * `<HillCard />` so the user can recognise projects at a glance. Two
 * pitches with identical titles but very different dot distributions are
 * instantly distinguishable.
 *
 * Purely presentational:
 * - No pointer or keyboard handlers. Interaction lives on the card's
 *   "Open" button.
 * - `aria-hidden`: the card's title and "X dots · Y done" description
 *   already carry the semantic information. Announcing 20 unlabelled
 *   circles to a screen reader adds noise without signal.
 * - Dots use `dot.color` unconditionally to match the main chart's
 *   rendering. Completion status is surfaced numerically in the card
 *   description ("3 done"), not through a colour override here — that
 *   would create a visual inconsistency between the preview and the
 *   live view.
 */
export function HillPreview({ dots }: HillPreviewProps) {
  return (
    <div className="border-slate-200 border-b bg-slate-50/60 px-4 py-3">
      <svg
        viewBox={`0 0 ${PREVIEW.width} ${PREVIEW.height}`}
        className="h-auto w-full"
        role="img"
        aria-hidden="true"
      >
        <title>Hill preview</title>
        {/* Baseline — a faint horizontal rule under the curve. Anchors
            dots that sit at x=0 or x=100 so they don't look like they're
            floating off the side of the curve. */}
        <line
          x1={PREVIEW.paddingX}
          x2={PREVIEW.width - PREVIEW.paddingX}
          y1={PREVIEW.height - PREVIEW.paddingY}
          y2={PREVIEW.height - PREVIEW.paddingY}
          className="stroke-slate-200"
          strokeWidth={1}
        />
        {/* Crest marker — dashed vertical at x=50. The Shape Up "hill
            crest" is the inflection point between scoping and executing;
            a subtle reference line reminds the user which side of the
            hill each dot is on. */}
        <line
          x1={previewX(50)}
          x2={previewX(50)}
          y1={PREVIEW.paddingY}
          y2={PREVIEW.height - PREVIEW.paddingY}
          className="stroke-slate-200"
          strokeWidth={1}
          strokeDasharray="2 2"
        />
        <path d={PREVIEW_PATH} fill="none" strokeWidth={1.5} className="stroke-slate-400" />
        {dots.map((dot) => (
          <circle
            // `dot.id` is safe as a key here because this component only
            // renders live hills (not snapshot projections). Live dot ids
            // are UUIDs, uniquely identifying the dot within the list.
            key={dot.id}
            cx={previewX(dot.x)}
            cy={previewY(dot.x)}
            r={3.5}
            fill={dot.color}
            className="stroke-white"
            strokeWidth={1}
          />
        ))}
      </svg>
    </div>
  );
}
